import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { NERA_AUTH_SERVICE } from 'app/core/tokens';
import { of, switchMap } from 'rxjs';

/**
 * No-auth guard.
 *
 * Redirects authenticated users away from auth pages (e.g. /sign-in, /sign-up).
 * Always allows:
 * - Routes starting with /public (public album viewing)
 * - /p/{code} (album access-grant entry — a signed-in photographer may open
 *   a client's link to preview it)
 * - Routes starting with /sign-out (sign-out flow)
 *
 * If authenticated and not on an allowed public route → redirects to '/' (root).
 * If not authenticated → allows navigation.
 *
 * @example
 * {
 *   path: 'sign-in',
 *   canActivate: [NoAuthGuard],
 *   loadComponent: () => import('./auth/sign-in/sign-in.component'),
 * }
 */
export const NoAuthGuard: CanActivateFn | CanActivateChildFn = (
    _route,
    state
) => {
    const router: Router = inject(Router);

    return inject(NERA_AUTH_SERVICE)
        .check()
        .pipe(
            switchMap((authenticate) => {
                // Allow public routes, grant entry and sign-out
                if (
                    state.url.startsWith('/public') ||
                    state.url.startsWith('/p/') ||
                    state.url.startsWith('/sign-out')
                ) {
                    return of(true);
                }
                if (authenticate.isAuthenticated) {
                    return of(router.parseUrl(''));
                }

                return of(true);
            })
        );
};
